import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import ApiCall from "../../utils/apicall";
import {
  Card,
  Stack,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Container,
  TableContainer,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { HomeRounded } from "@mui/icons-material";
import Iconify from "../../components/iconify";
import Label from "../../components/label";
import { openAuthedMedia } from "../../components/AuthedMedia/AuthedMedia";
import PieceIssuePanel from "../../components/PieceTrace/PieceIssuePanel"; // CR-2026-PIECE-TRACE
import HeatMtrPanel from "../../components/QDP/HeatMtrPanel"; // CR-2026-QDP
import CmtrReviewPanel from "../../components/CmtrReview/CmtrReviewPanel"; // CR-2026-CMTR-STAMP
import { hasPermission } from "../../utils/hasPermission";

import SmartTable from "../../components/SmartTable/SmartTable";
const STATUS_COLORS = {
  quarantine: "error",
  released: "success",
  consumed: "info",
};

function HeatDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);

  // State
  const [heat, setHeat] = useState(null);
  const [linkedPieces, setLinkedPieces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [linkPieceModal, setLinkPieceModal] = useState(false);
  const [availablePieces, setAvailablePieces] = useState([]);
  // Link and unlink run one at a time through this ref (the server does not refuse a duplicate link).
  const [linking, setLinking] = useState(false);
  const linkingRef = useRef(false);
  // Pieces linked in this session that no refreshed list has confirmed yet: kept out of the dialog.
  const [justLinkedIds, setJustLinkedIds] = useState([]);
  // Linked-pieces requests are numbered; only the latest one may update the list.
  const piecesSeq = useRef(0);
  // The heat whose linked list is on screen (set only by a successful load). Link and unlink stay
  // disabled until it is the current heat, so one heat's rows can never act on another.
  const [piecesLoadedFor, setPiecesLoadedFor] = useState(null);
  const piecesReady = piecesLoadedFor !== null && String(piecesLoadedFor) === String(id);
  // The heat this page shows now. Link/unlink results for a heat the user has left are dropped.
  const activeHeatId = useRef(id);
  activeHeatId.current = id;
  useEffect(() => {
    // A new heat: nothing opened for the previous one may stay open (a confirmation carries a
    // callback bound to the heat it was opened on).
    setJustLinkedIds([]);
    setLinkedPieces([]);
    setPiecesLoadedFor(null);
    setLinkPieceModal(false);
    setAvailablePieces([]);
    setConfirmDialog(null);
    setVerifyDialog(false);
  }, [id]);
  const [mtcFileInputRef] = useState(useRef(null));
  const [openingMtc, setOpeningMtc] = useState(false);
  // W-29 (Codex #80 P1): the CMTR file name captured when the verification dialog opened. If the heat's
  // CMTR changes while the dialog is open (a replace finished), the dialog closes and the checks reset.
  const [reviewedMtc, setReviewedMtc] = useState(null);
  // W-29 r3 (Codex #80 P1): the CMTR the reviewer actually opened (its stored name at the moment they clicked
  // View). Verification is only possible for that file and only while it is still the current one.
  const [openedMtc, setOpenedMtc] = useState(null);
  const [users, setUsers] = useState([]);
  const [approver, setApprover] = useState(loginUser?.decodedToken?.id);
  const [verifyDialog, setVerifyDialog] = useState(false);
  const [verifier, setVerifier] = useState(loginUser?.decodedToken?.id);
  const [verifyChecks, setVerifyChecks] = useState({
    heatNumber: false,
    gradeMatch: false,
    sizeShape: false,
  });
  const currentMtc = heat?.mtcFilename || null;
  // A different current CMTR invalidates what was opened for review.
  useEffect(() => {
    setOpenedMtc((o) => (o && o !== currentMtc ? null : o));
  }, [currentMtc]);
  useEffect(() => {
    if (verifyDialog && reviewedMtc && currentMtc !== reviewedMtc) {
      setVerifyDialog(false);
      setVerifyChecks({ heatNumber: false, gradeMatch: false, sizeShape: false });
      setReviewedMtc(null);
      setError("The CMTR changed while you were verifying it. Review the current CMTR, then verify again.");
    }
  }, [currentMtc, verifyDialog, reviewedMtc]);

  // Apply a refreshed linked list: it is the record, so the session-linked ids it now contains are
  // confirmed and dropped; any not yet in it stay hidden from the dialog.
  const applyLinkedPieces = (list, heatId) => {
    setLinkedPieces(list);
    setPiecesLoadedFor(String(heatId));
    const inList = new Set(list.map((p) => String(p.fabricatedItemId)));
    setJustLinkedIds((ids) => ids.filter((x) => !inList.has(x)));
  };

  // Re-read the linked list for heatId. Returns false if the request failed; a response that is not
  // the latest request, or is for a heat the user has left, is ignored (returns true: nothing to report).
  const refreshLinkedPieces = async (heatId) => {
    const seq = ++piecesSeq.current;
    try {
      const res = await ApiCall.get(`/heat/${heatId}/pieces`);
      if (seq !== piecesSeq.current) return true;
      if (String(activeHeatId.current) !== String(heatId)) return true;
      applyLinkedPieces(res.data?.data?.pieces || [], heatId);
      return true;
    } catch (err) {
      return seq !== piecesSeq.current;
    }
  };

  // Fetch heat details
  useEffect(() => {
    // Responses for a heat the user has already left are dropped.
    const heatId = id;
    const current = () => String(activeHeatId.current) === String(heatId);
    const fetchData = async () => {
      setLoading(true);
      try {
        const heatRes = await ApiCall.get(`/heat/${heatId}`);
        if (!current()) return;
        setHeat(heatRes.data?.data?.heat);

        const seq = ++piecesSeq.current;
        const piecesRes = await ApiCall.get(`/heat/${heatId}/pieces`);
        if (!current()) return;
        if (seq === piecesSeq.current) applyLinkedPieces(piecesRes.data?.data?.pieces || [], heatId);

        setError(null);
      } catch (err) {
        if (current()) setError(err.response?.data?.message || "Failed to load heat details");
      } finally {
        if (current()) setLoading(false);
      }
    };

    if (id) fetchData();
  }, [id]);

  // Fetch users for approver/verifier dropdowns
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await ApiCall.get("/user");
        const raw = res.data?.data?.users;
        setUsers(Array.isArray(raw) ? raw : []);
      } catch (err) {
        // non-critical
      }
    };
    fetchUsers();
  }, []);

  // Handle CMTR verification
  const handleVerifyCMTR = async () => {
    try {
      // W-29: name the CMTR file the reviewer saw (captured when the dialog opened). The server refuses
      // (409) if it is no longer the current one, so a verification can never land on a file nobody reviewed.
      if (!reviewedMtc) { setError("Open the verification again: no CMTR was captured for review."); return; }
      const res = await ApiCall.patch(`/heat/${id}/verify-cmtr`, {
        verifiedBy: verifier,
        mtcFilename: reviewedMtc || undefined,
      });
      setHeat(res.data?.data?.heat);
      setVerifyDialog(false);
      setVerifyChecks({ heatNumber: false, gradeMatch: false, sizeShape: false });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to verify CMTR");
    }
  };

  // Handle release
  const handleRelease = async () => {
    try {
      await ApiCall.patch(`/heat/${id}/release`, {
        releasedBy: approver,
      });
      const res = await ApiCall.get(`/heat/${id}`);
      setHeat(res.data?.data?.heat);
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to release heat");
    }
  };

  // Handle quarantine
  const handleQuarantine = async () => {
    try {
      await ApiCall.patch(`/heat/${id}/quarantine`);
      const res = await ApiCall.get(`/heat/${id}`);
      setHeat(res.data?.data?.heat);
      setConfirmDialog(null);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to quarantine heat");
    }
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await ApiCall.delete(`/heat/${id}`);
      navigate(-1);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to delete heat");
    }
  };

  // Open link piece modal
  const handleOpenLinkPieceModal = async () => {
    // The list belongs to this heat's job: a response that arrives after the user moved to another
    // heat is dropped, so it can never be offered for linking there.
    const heatId = id;
    const current = () => String(activeHeatId.current) === String(heatId);
    // The loaded heat must be this page's heat (not the previous one still on screen while loading).
    if (!heat || (heat.id != null && String(heat.id) !== String(heatId)) || loading || !piecesReady) {
      setError("This heat is still loading. Try again in a moment.");
      return;
    }
    try {
      const res = await ApiCall.get(
        `/fabricated-items/job/${heat.jobId}`
      );
      if (!current()) return;
      // W-22 (2026-10-06): GET /fabricated-items/job/:jobId answers a plain list (data: [...]).
      const d = res.data?.data;
      const list = Array.isArray(d) ? d : d?.FabricatedItems || [];
      setAvailablePieces(list.filter((p) => p && p.id != null));
      setLinkPieceModal(true);
    } catch (err) {
      if (current()) setError("Failed to load available pieces");
    }
  };

  // Link a piece. A piece already linked (or linked moments ago) is not offered; link and unlink run one
  // at a time. A failed refresh after a successful link is reported as such, not as a failed link.
  const handleLinkPiece = async (piece) => {
    if (linkingRef.current) return;
    const heatId = id;
    const current = () => String(activeHeatId.current) === String(heatId);
    linkingRef.current = true;
    setLinking(true);
    try {
      try {
        // W-22: POST /heat/:id/link-piece { fabricatedItemId } (POST /heat/:id/pieces does not exist).
        await ApiCall.post(`/heat/${heatId}/link-piece`, { fabricatedItemId: piece.id });
      } catch (err) {
        if (current()) setError(err.response?.data?.message || "Failed to link piece");
        return;
      }
      if (!current()) return;
      setJustLinkedIds((ids) => [...ids, String(piece.id)]);
      setLinkPieceModal(false);
      const ok = await refreshLinkedPieces(heatId);
      if (!ok && current()) setError("Linked, but the list could not be refreshed. Reload the page.");
    } finally {
      linkingRef.current = false;
      setLinking(false);
    }
  };

  const linkedIds = new Set([
    ...linkedPieces.map((p) => String(p.fabricatedItemId)),
    ...justLinkedIds,
  ]);
  const linkablePieces = availablePieces.filter((p) => !linkedIds.has(String(p.id)));

  // Unlink piece. W-22 (2026-10-06): the server route is DELETE /heat/:id/link-piece/:fabricatedItemId
  // (DELETE /heat/:id/pieces/:id does not exist, so unlinking always failed).
  const handleUnlinkPiece = async (fabricatedItemId, openedOnHeatId) => {
    setConfirmDialog(null);
    // The confirmation must belong to the heat on screen; otherwise nothing is sent.
    if (String(openedOnHeatId) !== String(activeHeatId.current) || !piecesReady) return;
    if (linkingRef.current) {
      setError("Another link change is still running. Try again in a moment.");
      return;
    }
    const heatId = id;
    const current = () => String(activeHeatId.current) === String(heatId);
    linkingRef.current = true;
    setLinking(true);
    try {
      try {
        await ApiCall.delete(`/heat/${heatId}/link-piece/${fabricatedItemId}`);
      } catch (err) {
        if (current()) setError(err.response?.data?.message || "Failed to unlink piece");
        return;
      }
      if (!current()) return;
      setJustLinkedIds((ids) => ids.filter((x) => x !== String(fabricatedItemId)));
      const ok = await refreshLinkedPieces(heatId);
      if (!ok && current()) setError("Unlinked, but the list could not be refreshed. Reload the page.");
    } finally {
      linkingRef.current = false;
      setLinking(false);
    }
  };

  // Upload MTC document
  const handleMtcUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const fd = new FormData();
      fd.append("mtcFile", file);
      await ApiCall.post(`/heat/${id}/mtc`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const res = await ApiCall.get(`/heat/${id}`);
      setHeat(res.data?.data?.heat);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to upload CMTR");
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography sx={{ p: 3 }}>Loading heat details...</Typography>
      </Container>
    );
  }

  if (!heat) {
    return (
      <Container>
        <Typography variant="h6" color="error">
          Heat not found
        </Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  // W-29: a CMTR is on file when the server stored one (mtcFilename); mtcFileUrl (sent by the server
  // since W-29) is what the View button opens, through openAuthedMedia (S-70 gate: token + company).
  const hasCmtr = !!(heat.mtcFileUrl || heat.mtcFilename);
  // CR-2026-CMTR-STAMP: each CMTR action shows only to the duty that may do it (the server checks the same,
  // live): receiving attaches (add_heat); QC reviews (review_cmtr). A rejected CMTR is not verified; it is replaced.
  const userPerms = loginUser?.decodedToken?.permissions || [];
  const canAttachCmtr = hasPermission(userPerms, "add_heat");
  const canReviewCmtr = hasPermission(userPerms, "review_cmtr");
  const cmtrRejected = heat.cmtr_review_status === "rejected";

  return (
    <div>
      <Helmet>
        <title>Heat Detail | SFM</title>
      </Helmet>

      <Container maxWidth="lg">
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <div>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <HomeRounded color="inherit" />
                <Typography variant="body1" color="textPrimary">
                  / Heat Detail
                </Typography>
              </Stack>
            </Breadcrumbs>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h4">{heat.heatNumber}</Typography>
              <Label color={STATUS_COLORS[heat.status] || "default"}>
                {heat.status}
              </Label>
            </Stack>
          </div>
          <Button
            variant="outlined"
            startIcon={<Iconify icon="eva:arrow-ios-back-outline" />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Stack>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: "error.light" }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          {heat.status === "quarantine" && (
            <Tooltip
              title={
                !hasCmtr
                  ? "Upload the CMTR before releasing"
                  : !heat.cmtrVerified
                  ? "CMTR must be verified compliant before releasing"
                  : ""
              }
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  disabled={!hasCmtr || !heat.cmtrVerified}
                  startIcon={<Iconify icon="eva:checkmark-outline" />}
                  onClick={() => {
                    setApprover(loginUser?.decodedToken?.id);
                    setConfirmDialog({
                      title: "Release Heat to Fabrication?",
                      message: `Release heat ${heat.heatNumber} (${heat.grade}) to fabrication? CMTR has been verified compliant. Select the authorizing engineer below.`,
                      onConfirm: handleRelease,
                      requiresApprover: true,
                    });
                  }}
                >
                  Release to Fab
                </Button>
              </span>
            </Tooltip>
          )}
          {heat.status === "released" && (
            <Button
              variant="contained"
              color="warning"
              startIcon={<Iconify icon="eva:alert-circle-outline" />}
              onClick={() =>
                setConfirmDialog({
                  title: "Quarantine Heat?",
                  message: `Quarantine heat ${heat.heatNumber}?`,
                  onConfirm: handleQuarantine,
                })
              }
            >
              Quarantine
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={<Iconify icon="eva:trash-2-outline" />}
            onClick={() =>
              setConfirmDialog({
                title: "Delete Heat?",
                message: `Delete heat ${heat.heatNumber}? This cannot be undone.`,
                onConfirm: handleDelete,
              })
            }
          >
            Delete
          </Button>
        </Stack>

        {/* Info Card */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Heat Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Grade
              </Typography>
              <Typography variant="body1">{heat.grade || "-"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Supplier
              </Typography>
              <Typography variant="body1">{heat.supplier || "-"}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Quantity
              </Typography>
              <Typography variant="body1">
                {heat.quantity != null ? `${heat.quantity} ${heat.unit || ""}`.trim() : "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Received Date
              </Typography>
              <Typography variant="body1">
                {heat.receivedDate
                  ? new Date(heat.receivedDate).toLocaleDateString()
                  : "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="textSecondary">
                Received By
              </Typography>
              <Typography variant="body1">{heat.receivedBy || "-"}</Typography>
            </Grid>
            {heat.releasedDate && (
              <>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Released Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(heat.releasedDate).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="textSecondary">
                    Released By
                  </Typography>
                  <Typography variant="body1">
                    {heat.releaser
                      ? `${heat.releaser.firstName} ${heat.releaser.lastName}`
                      : heat.releasedBy || "—"}
                  </Typography>
                </Grid>
              </>
            )}
            {heat.notes && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  Notes
                </Typography>
                <Typography variant="body1">{heat.notes}</Typography>
              </Grid>
            )}
          </Grid>
        </Card>

        {/* CMTR Document Section */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="h6">Certified Mill Test Report (CMTR)</Typography>
            {heat.cmtrVerified && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Iconify icon="eva:checkmark-circle-2-outline" sx={{ color: "success.main", width: 20, height: 20 }} />
                <Typography variant="caption" color="success.main" fontWeight={600}>
                  Verified Compliant
                </Typography>
              </Stack>
            )}
          </Stack>
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 2 }}>
            The CMTR documents this heat's chemical composition, mechanical properties, and grade certification from the mill.
          </Typography>

          {hasCmtr ? (
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Iconify icon="eva:file-pdf-outline" sx={{ width: 24, height: 24 }} />
              <Typography variant="body1">{heat.mtcFileName || heat.mtcFilename}</Typography>
              {/* S-70: /uploads (heat_mtcs, heats/...) is behind sign-in and a company check, and a
                  plain href sends no Bearer token. Fetch with the token and open a blob instead. */}
              {heat.mtcFileUrl ? (
              <Button
                variant="outlined"
                size="small"
                disabled={openingMtc}
                onClick={async () => {
                  // Codex #78 P2: one fetch at a time, with visible progress.
                  if (openingMtc) return;
                  setOpeningMtc(true);
                  const opening = heat.mtcFilename || null;   // the file this click opens
                  try { if (await openAuthedMedia(heat.mtcFileUrl)) setOpenedMtc(opening); } finally { setOpeningMtc(false); }
                }}
              >
                {openingMtc ? "Opening…" : "View / Download CMTR"}
              </Button>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  On record, but the file cannot be opened from here.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="error.main" sx={{ mb: 2 }}>
              No CMTR attached — heat cannot be released to fabrication until the CMTR is uploaded.
            </Typography>
          )}

          {heat.cmtrVerified ? (
            <Box sx={{ p: 1.5, bgcolor: "success.lighter", borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="success.dark">
                ✓ Heat number confirmed &nbsp;|&nbsp; ✓ Grade/material confirmed &nbsp;|&nbsp; ✓ Size/shape confirmed
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Verified by {heat.cmtrVerifier ? `${heat.cmtrVerifier.firstName} ${heat.cmtrVerifier.lastName}` : "—"}
                {heat.cmtrVerifiedAt ? ` on ${new Date(heat.cmtrVerifiedAt).toLocaleDateString()}` : ""}
              </Typography>
            </Box>
          ) : hasCmtr ? (
            <Box sx={{ p: 1.5, bgcolor: "warning.lighter", borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" color="warning.dark">
                CMTR uploaded but not yet verified. A qualified reviewer must confirm compliance before this heat can be released.
              </Typography>
            </Box>
          ) : null}

          <CmtrReviewPanel
            heat={heat}
            openedMtc={openedMtc}
            onHeatChanged={async () => {
              const res = await ApiCall.get(`/heat/${id}`);
              if (String(activeHeatId.current) === String(id)) setHeat(res.data?.data?.heat);
            }}
          />

          <Divider sx={{ my: 2 }} />
          <Stack direction="row" spacing={1}>
            <input
              type="file"
              ref={mtcFileInputRef}
              onChange={handleMtcUpload}
              accept=".pdf,.jpg,.jpeg,.png"
              style={{ display: "none" }}
            />
            {canAttachCmtr && (
            <Button
              variant="outlined"
              onClick={() => mtcFileInputRef.current?.click()}
            >
              {hasCmtr ? "Replace CMTR" : "Upload CMTR"}
            </Button>
            )}
            {hasCmtr && !heat.cmtrVerified && canReviewCmtr && !cmtrRejected && (
              <Button
                variant="contained"
                color="warning"
                onClick={() => {
                  setVerifier(loginUser?.decodedToken?.id);
                  setVerifyChecks({ heatNumber: false, gradeMatch: false, sizeShape: false });
                  if (!openedMtc || openedMtc !== heat.mtcFilename) {
                    setError("Open the current CMTR (View / Download CMTR) before verifying it.");
                    return;
                  }
                  setReviewedMtc(openedMtc);   // W-29 (Codex #80 P1): the file the reviewer opened
                  setVerifyDialog(true);
                }}
              >
                Verify CMTR Compliance
              </Button>
            )}
          </Stack>
        </Card>

        {/* Linked Pieces Section */}
        <Card>
          <Box sx={{ p: 3, pb: 0 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Linked Pieces</Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={handleOpenLinkPieceModal}
                disabled={linking || !piecesReady}
              >
                Link Piece
              </Button>
            </Stack>
          </Box>
          {!piecesReady && !loading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="error">
                The linked pieces for this heat could not be loaded. Reload the page.
              </Typography>
            </Box>
          ) : linkedPieces.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary">
                No pieces linked to this heat
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <SmartTable tableId="heat-issues" lockedColumns={["Mark", "Issued Date"]}>
                <TableHead>
                  <TableRow>
                    <TableCell>Mark</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Issued Date</TableCell>
                    <TableCell>Issued By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedPieces.map((piece) => (
                    <TableRow key={piece.id} hover>
                      {/* W-22: the server sends link rows (fabricatedItemId, issuedAt, notes) with the piece
                          and the issuer included; mark / description / issuedDate were never sent. */}
                      <TableCell>{piece.fabricated_items_perjob?.name || piece.mark || "-"}</TableCell>
                      <TableCell>{piece.notes || piece.description || ""}</TableCell>
                      <TableCell>
                        {piece.issuedAt || piece.issuedDate
                          ? new Date(piece.issuedAt || piece.issuedDate).toLocaleDateString()
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {piece.issuer
                          ? [piece.issuer.firstName, piece.issuer.lastName].filter(Boolean).join(" ") || piece.issuer.email
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <MenuItem
                          sx={{ color: "error.main" }}
                          disabled={linking || !piecesReady}
                          onClick={() =>
                            setConfirmDialog({
                              title: "Unlink Piece?",
                              message: `Unlink piece ${piece.fabricated_items_perjob?.name || piece.mark || piece.fabricatedItemId}?`,
                              onConfirm: () => handleUnlinkPiece(piece.fabricatedItemId, id),
                            })
                          }
                        >
                          <Iconify icon="eva:trash-2-outline" />
                        </MenuItem>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </SmartTable>
            </TableContainer>
          )}
        </Card>
        <PieceIssuePanel heat={heat} />
        <HeatMtrPanel heatId={heat?.id} />
      </Container>

      {/* Link Piece Modal */}
      <Dialog
        open={linkPieceModal}
        onClose={() => { if (!linkingRef.current) setLinkPieceModal(false); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Link Piece to Heat</DialogTitle>
        <DialogContent>
          {linkablePieces.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
              {availablePieces.length === 0
                ? "No fabricated items available to link"
                : "Every fabricated item on this job is already linked to this heat"}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, py: 2 }}>
              {linkablePieces.map((piece) => (
                <Paper
                  key={piece.id}
                  aria-disabled={linking}
                  sx={{
                    p: 2,
                    cursor: linking ? "default" : "pointer",
                    opacity: linking ? 0.5 : 1,
                    pointerEvents: linking ? "none" : "auto",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                  onClick={() => handleLinkPiece(piece)}
                >
                  {/* W-22: fabricated items carry name / section / grade / length (no mark or description). */}
                  <Typography variant="body2" fontWeight="bold">
                    {piece.name || piece.mark || `Item ${piece.id}`}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {[piece.section, piece.grade, piece.length, piece.quantity != null ? `Qty ${piece.quantity}` : null]
                      .filter((v) => v !== null && v !== undefined && v !== "")
                      .join(" · ") || piece.description || ""}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkPieceModal(false)} disabled={linking}>
            {linking ? "Linking..." : "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <Dialog
          open={!!confirmDialog}
          onClose={() => setConfirmDialog(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{confirmDialog.title}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: confirmDialog.requiresApprover ? 2 : 0 }}>
              {confirmDialog.message}
            </Typography>
            {confirmDialog.requiresApprover && (
              <FormControl fullWidth size="small">
                <InputLabel>Released by</InputLabel>
                <Select
                  label="Released by"
                  value={approver}
                  onChange={(e) => setApprover(e.target.value)}
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.firstName} {u.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
            <Button
              onClick={() => confirmDialog.onConfirm()}
              variant="contained"
              color="primary"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* CMTR Verification Dialog */}
      <Dialog
        open={verifyDialog}
        onClose={() => setVerifyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify CMTR Compliance</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Review the CMTR document and confirm each item below before marking this heat as compliant.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={verifyChecks.heatNumber}
                  onChange={(e) => setVerifyChecks((p) => ({ ...p, heatNumber: e.target.checked }))}
                />
              }
              label={`Heat number on CMTR matches: ${heat?.heatNumber || "—"}`}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={verifyChecks.gradeMatch}
                  onChange={(e) => setVerifyChecks((p) => ({ ...p, gradeMatch: e.target.checked }))}
                />
              }
              label={`Material grade confirmed compliant: ${heat?.grade || "—"}`}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={verifyChecks.sizeShape}
                  onChange={(e) => setVerifyChecks((p) => ({ ...p, sizeShape: e.target.checked }))}
                />
              }
              label="Material size and shape confirmed as ordered"
            />
          </Box>
          <FormControl fullWidth size="small">
            <InputLabel>Verified by</InputLabel>
            <Select
              label="Verified by"
              value={verifier}
              onChange={(e) => setVerifier(e.target.value)}
            >
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* CR-2026-CMTR-STAMP */}
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 1.5 }}>
            Marking compliant files a stamped ACCEPTED review copy signed by you; the original CMTR file is not changed.
            Document review only: the stamp does not certify the material or witness the mill&apos;s tests.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerifyDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!verifyChecks.heatNumber || !verifyChecks.gradeMatch || !verifyChecks.sizeShape}
            onClick={handleVerifyCMTR}
          >
            Mark CMTR Compliant
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default HeatDetail;
